import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  Save,
  Globe,
  ArrowLeft,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Upload,
  Loader2,
  Image as ImageIcon,
  Clock,
  MessageSquare,
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import { INDUSTRIES, DEFAULT_HOURS } from '../../utils/industrySchemas'
import { useAuth } from '../../context/AuthContext'

// ─── Field renderers ─────────────────────────────────────────────────────────

const TextField = ({ field, value, onChange }) => (
  <div>
    <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.375rem' }}>
      {field.label}
    </label>
    <input
      type={field.type === 'url' ? 'url' : 'text'}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder}
      className="input w-full"
      style={{ height: '2.25rem', fontSize: '0.875rem' }}
    />
  </div>
)

const TextAreaField = ({ field, value, onChange }) => (
  <div>
    <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.375rem' }}>
      {field.label}
    </label>
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder}
      rows={4}
      className="input w-full"
      style={{ resize: 'vertical', fontSize: '0.875rem' }}
    />
  </div>
)

const ImageField = ({ field, value, onChange, clientId }) => {
  const [uploading, setUploading] = useState(false)

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const form = new FormData()
      form.append('files', file)
      if (clientId) form.append('clientId', clientId)
      const res = await apiService.post('/media/upload', form)
      onChange(res.data.files?.[0]?.public_url)
      toast.success('Image uploaded')
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.375rem' }}>
        {field.label}
      </label>
      <div className="flex items-center gap-3">
        {value ? (
          <div style={{ position: 'relative', width: '5rem', height: '5rem', borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)' }}
            className="group"
          >
            <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <button
              onClick={() => onChange('')}
              style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', opacity: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.15s', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0'}
            >
              <Trash2 style={{ width: '1rem', height: '1rem', color: 'white' }} />
            </button>
          </div>
        ) : (
          <div style={{ width: '5rem', height: '5rem', borderRadius: 'var(--radius)', border: '2px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ImageIcon style={{ width: '1.5rem', height: '1.5rem', color: 'var(--text-faint)' }} />
          </div>
        )}
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          borderRadius: 'var(--radius)',
          fontSize: '0.875rem',
          fontWeight: 500,
          cursor: uploading ? 'not-allowed' : 'pointer',
          border: '1px solid var(--border)',
          background: 'var(--surface)',
          color: 'var(--text-soft)',
          transition: 'all 0.15s',
          opacity: uploading ? 0.5 : 1,
        }}>
          {uploading ? <Loader2 style={{ width: '1rem', height: '1rem', animation: 'spin 0.8s linear infinite' }} /> : <Upload style={{ width: '1rem', height: '1rem' }} />}
          {uploading ? 'Uploading...' : 'Upload image'}
          <input type="file" accept="image/*" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', border: 0 }} onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
    </div>
  )
}

const MultiImageField = ({ field, value = [], onChange, clientId }) => {
  const [uploading, setUploading] = useState(false)

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setUploading(true)
    try {
      const urls = await Promise.all(files.map(async (file) => {
        const form = new FormData()
        form.append('files', file)
        if (clientId) form.append('clientId', clientId)
        const res = await apiService.post('/media/upload', form)
        return res.data.files?.[0]?.public_url
      }))
      onChange([...value, ...urls])
      toast.success(`${urls.length} image(s) uploaded`)
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const removeImage = (index) => onChange(value.filter((_, i) => i !== index))

  return (
    <div>
      <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.375rem' }}>
        {field.label}
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '0.75rem' }}>
        {value.map((url, i) => (
          <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)' }}>
            <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <button
              onClick={() => removeImage(i)}
              style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', opacity: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.15s', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0'}
            >
              <Trash2 style={{ width: '1rem', height: '1rem', color: 'white' }} />
            </button>
          </div>
        ))}
        <label style={{
          aspectRatio: '1',
          borderRadius: 'var(--radius)',
          border: '2px dashed var(--border)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: uploading ? 'not-allowed' : 'pointer',
          transition: 'all 0.15s',
          opacity: uploading ? 0.5 : 1,
        }}>
          {uploading ? <Loader2 style={{ width: '1.25rem', height: '1.25rem', animation: 'spin 0.8s linear infinite', color: 'var(--text-faint)' }} /> : <Plus style={{ width: '1.25rem', height: '1.25rem', color: 'var(--text-faint)' }} />}
          <input type="file" accept="image/*" multiple style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', border: 0 }} onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
    </div>
  )
}

const HoursField = ({ value = DEFAULT_HOURS, onChange }) => (
  <div>
    <div style={{ overflow: 'hidden', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
      <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--surface-2)' }}>
            <th className="text-soft font-medium" style={{ textAlign: 'left', padding: '0.625rem 1rem' }}>Day</th>
            <th className="text-soft font-medium" style={{ textAlign: 'left', padding: '0.625rem 1rem' }}>Status</th>
            <th className="text-soft font-medium" style={{ textAlign: 'left', padding: '0.625rem 1rem' }}>Opens</th>
            <th className="text-soft font-medium" style={{ textAlign: 'left', padding: '0.625rem 1rem' }}>Closes</th>
          </tr>
        </thead>
        <tbody>
          {value.map((row, i) => (
            <tr key={row.day} style={{ borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
              <td className="font-medium" style={{ padding: '0.625rem 1rem', color: 'var(--text)' }}>{row.day}</td>
              <td style={{ padding: '0.625rem 1rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    const updated = [...value]
                    updated[i] = { ...row, open: !row.open }
                    onChange(updated)
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.25rem 0.625rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    background: row.open ? 'var(--success-soft)' : 'var(--surface-2)',
                    color: row.open ? 'var(--success)' : 'var(--text-muted)',
                  }}
                >
                  {row.open ? 'Open' : 'Closed'}
                </button>
              </td>
              <td style={{ padding: '0.625rem 1rem' }}>
                <input
                  type="time"
                  value={row.openTime}
                  disabled={!row.open}
                  onChange={e => {
                    const updated = [...value]
                    updated[i] = { ...row, openTime: e.target.value }
                    onChange(updated)
                  }}
                  className="input"
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem', opacity: row.open ? 1 : 0.4, cursor: row.open ? 'auto' : 'not-allowed' }}
                />
              </td>
              <td style={{ padding: '0.625rem 1rem' }}>
                <input
                  type="time"
                  value={row.closeTime}
                  disabled={!row.open}
                  onChange={e => {
                    const updated = [...value]
                    updated[i] = { ...row, closeTime: e.target.value }
                    onChange(updated)
                  }}
                  className="input"
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem', opacity: row.open ? 1 : 0.4, cursor: row.open ? 'auto' : 'not-allowed' }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)

// ─── List field (repeatable items) ────────────────────────────────────────────

const ListField = ({ fieldDef, value = [], onChange, clientId }) => {
  const [expanded, setExpanded] = useState(null)

  const addItem = () => {
    const newItem = {}
    fieldDef.subFields.forEach(sf => { newItem[sf.key] = sf.type === 'multi-image' ? [] : '' })
    const updated = [...value, newItem]
    onChange(updated)
    setExpanded(updated.length - 1)
  }

  const removeItem = (index) => {
    onChange(value.filter((_, i) => i !== index))
    if (expanded === index) setExpanded(null)
  }

  const updateItem = (index, key, val) => {
    const updated = [...value]
    updated[index] = { ...updated[index], [key]: val }
    onChange(updated)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {value.map((item, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
          {/* Item header */}
          <div className="flex items-center justify-between" style={{ padding: '0.75rem 1rem', background: 'var(--surface-2)' }}>
            <button
              type="button"
              onClick={() => setExpanded(expanded === i ? null : i)}
              className="flex items-center gap-2 font-medium text-soft"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem' }}
            >
              {expanded === i ? <ChevronUp style={{ width: '1rem', height: '1rem' }} /> : <ChevronDown style={{ width: '1rem', height: '1rem' }} />}
              {item[fieldDef.subFields[0]?.key] || `Item ${i + 1}`}
            </button>
            <button
              type="button"
              onClick={() => removeItem(i)}
              className="btn-icon"
              style={{ padding: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', borderRadius: 'var(--radius-sm)', transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}
            >
              <Trash2 style={{ width: '0.875rem', height: '0.875rem' }} />
            </button>
          </div>

          {/* Item fields */}
          {expanded === i && (
            <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem', background: 'var(--surface)' }}>
              {fieldDef.subFields.map(sf => (
                <div key={sf.key}>
                  {sf.type === 'text' || sf.type === 'url' ? (
                    <TextField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} />
                  ) : sf.type === 'textarea' ? (
                    <TextAreaField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} />
                  ) : sf.type === 'image' ? (
                    <ImageField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} clientId={clientId} />
                  ) : sf.type === 'multi-image' ? (
                    <MultiImageField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} clientId={clientId} />
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <button type="button" className="btn btn-ghost btn-sm w-full" style={{ gap: '0.5rem' }} onClick={addItem}>
        <Plus style={{ width: '1rem', height: '1rem' }} />
        {fieldDef.addLabel || 'Add Item'}
      </button>
    </div>
  )
}

// ─── Field dispatcher ─────────────────────────────────────────────────────────

const FieldRenderer = ({ field, value, onChange, clientId }) => {
  switch (field.type) {
    case 'text':
    case 'url':
      return <TextField field={field} value={value} onChange={onChange} />
    case 'textarea':
      return <TextAreaField field={field} value={value} onChange={onChange} />
    case 'image':
      return <ImageField field={field} value={value} onChange={onChange} clientId={clientId} />
    case 'multi-image':
      return <MultiImageField field={field} value={value} onChange={onChange} clientId={clientId} />
    case 'hours':
      return <HoursField value={value} onChange={onChange} />
    case 'list':
      return <ListField fieldDef={field} value={value} onChange={onChange} clientId={clientId} />
    default:
      return null
  }
}

// ─── Main ContentEditor ────────────────────────────────────────────────────────

const ContentEditor = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const siteId = searchParams.get('site')

  const [activeTab,  setActiveTab]  = useState(null)
  const [content,    setContent]    = useState({})
  const [hasUnsaved, setHasUnsaved] = useState(false)

  const { data: site, isLoading: siteLoading } = useQuery(
    ['site', siteId],
    () => apiService.get(`/sites/${siteId}`).then(r => r.data),
    { enabled: !!siteId }
  )

  const { data: clientProfile, isLoading: clientLoading } = useQuery(
    ['client-profile'],
    () => apiService.get('/clients/profile').then(r => r.data),
  )

  useEffect(() => {
    if (site?.meta_data) {
      try {
        const parsed = typeof site.meta_data === 'string' ? JSON.parse(site.meta_data) : site.meta_data
        setContent(parsed || {})
      } catch {
        setContent({})
      }
    }
  }, [site])

  const industry = clientProfile?.industry || 'general'
  const schema   = INDUSTRIES[industry] || INDUSTRIES.general

  useEffect(() => {
    if (schema && !activeTab) setActiveTab(schema.tabs[0]?.id)
  }, [schema])

  const updateField = useCallback((key, value) => {
    setContent(prev => ({ ...prev, [key]: value }))
    setHasUnsaved(true)
  }, [])

  const saveMutation = useMutation(
    (meta) => apiService.put(`/sites/${siteId}`, { meta_data: JSON.stringify(meta) }),
    {
      onSuccess: () => {
        toast.success('Content saved!')
        setHasUnsaved(false)
        queryClient.invalidateQueries(['site', siteId])
      },
      onError: () => toast.error('Failed to save content'),
    }
  )

  const publishMutation = useMutation(
    async () => {
      await saveMutation.mutateAsync(content)
      return apiService.post(`/sites/${siteId}/publish`)
    },
    {
      onSuccess: () => toast.success('Site published!'),
      onError:   () => toast.error('Failed to publish'),
    }
  )

  const handleSave    = () => saveMutation.mutate(content)
  const handlePublish = () => publishMutation.mutate()

  if (siteLoading || clientLoading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '16rem' }}>
        <LoadingSpinner size="lg" text="Loading editor..." />
      </div>
    )
  }

  if (!siteId) {
    return (
      <div className="flex items-center justify-center" style={{ height: '16rem', flexDirection: 'column', gap: '1rem' }}>
        <p className="text-muted">No site selected.</p>
        <button className="btn btn-ghost" onClick={() => navigate('/client/sites')}>Back to Sites</button>
      </div>
    )
  }

  const activeTabSchema = schema.tabs.find(t => t.id === activeTab)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
          <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
            <button
              className="btn btn-ghost btn-icon btn-sm"
              onClick={() => navigate('/client/sites')}
              style={{ flexShrink: 0 }}
            >
              <ArrowLeft style={{ width: '1rem', height: '1rem' }} />
            </button>
            <div style={{ minWidth: 0 }}>
              <h1 className="font-bold truncate" style={{ fontSize: '1.25rem', color: 'var(--text)', margin: 0 }}>
                {site?.name || 'Edit Content'}
              </h1>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.125rem' }}>
                {schema.icon} {schema.label} — update your website content below
              </p>
            </div>
            {hasUnsaved && (
              <span className="font-medium" style={{ flexShrink: 0, fontSize: '0.75rem', color: 'var(--warning)' }}>• Unsaved</span>
            )}
          </div>

          <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => navigate('/client/requests/new')}
              style={{ gap: '0.375rem', fontSize: '0.75rem' }}
            >
              <MessageSquare style={{ width: '0.875rem', height: '0.875rem' }} />
              <span>Request a Change</span>
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleSave}
              disabled={saveMutation.isLoading}
              style={{ gap: '0.375rem' }}
            >
              {saveMutation.isLoading ? <Loader2 style={{ width: '0.875rem', height: '0.875rem', animation: 'spin 0.8s linear infinite' }} /> : <Save style={{ width: '0.875rem', height: '0.875rem' }} />}
              Save
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={handlePublish}
              disabled={publishMutation.isLoading}
              style={{ gap: '0.375rem' }}
            >
              {publishMutation.isLoading ? <Loader2 style={{ width: '0.875rem', height: '0.875rem', animation: 'spin 0.8s linear infinite' }} /> : <Globe style={{ width: '0.875rem', height: '0.875rem' }} />}
              Publish
            </button>
          </div>
        </div>
      </div>

      {/* Mobile tab bar */}
      <div style={{ display: 'flex', overflowX: 'auto', gap: '0.25rem', paddingBottom: '0.25rem' }} className="hidden-sm">
        {schema.tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flexShrink: 0,
              padding: '0.5rem 0.75rem',
              borderRadius: 'var(--radius)',
              fontSize: '0.875rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.15s',
              background: activeTab === tab.id ? 'var(--accent)' : 'transparent',
              color: activeTab === tab.id ? 'white' : 'var(--text-soft)',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '1.5rem' }}>
        {/* Sidebar tabs — desktop */}
        <nav style={{ width: '12rem', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {schema.tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '0.625rem 0.75rem',
                borderRadius: 'var(--radius)',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.15s',
                background: activeTab === tab.id ? 'var(--accent)' : 'transparent',
                color: activeTab === tab.id ? 'white' : 'var(--text-soft)',
              }}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Tab content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {activeTabSchema && (
            <div className="card card-pad">
              <h2 className="font-semibold" style={{ fontSize: '1.125rem', color: 'var(--text)', marginBottom: '1.25rem' }}>{activeTabSchema.label}</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {activeTabSchema.fields.map(field => (
                  <FieldRenderer
                    key={field.key}
                    field={field}
                    value={content[field.key]}
                    onChange={val => updateField(field.key, val)}
                    clientId={clientProfile?.id}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ContentEditor
